import React, { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Empty, SectionHead, Stat, EvidenceBadge, Banner } from '../components/ui';
import { ActionForm } from '../components/ActionForm';
import { TodayCommitted } from '../components/TodayCommitted';
import { EMPTY_COPY } from '../lib/constants';
import { fmtDate } from '../lib/dates';
import { isOverdue } from '../lib/actions';
import { fireCrmWriteback } from '../state/api';
import { crmWritebackForAccept } from '../lib/crm';
import { orgWorkstreams } from '../lib/orgPlan';

function priorityTone(p) {
  if (p === 'Critical') return 'red';
  if (p === 'High') return 'amber';
  return '';
}

function statusTone(s) {
  if (s === 'Blocked') return 'red';
  if (s === 'Closed' || s === 'Completed') return 'green';
  if (s === 'Today' || s === 'In progress' || s === 'In Progress') return 'amber';
  return '';
}

function sourceLabel(source) {
  if (source === 'BridgeAI') return 'Bridge AI';
  if (source === 'Rule') return 'Rule';
  if (source === 'Manual') return 'Manual';
  return source || 'Rule';
}

export default function Actions({ go }) {
  const { state, d, patch } = useStore();
  const actions = d.top3;
  const workstreamOptions = orgWorkstreams(state).map((w) => w.label);
  const [workstream, setWorkstream] = useState('All');
  const [modal, setModal] = useState(null);
  const [packagesOpen, setPackagesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const filterWs = (rows) => (workstream === 'All' ? rows : (rows || []).filter((a) => a.workstream === workstream));

  const readyNext = useMemo(() => filterWs(actions.readyNext || []), [actions.readyNext, workstream]);
  const initialization = useMemo(() => filterWs(actions.initialization || []), [actions.initialization, workstream]);
  const blocked = useMemo(() => filterWs(actions.blocked || []), [actions.blocked, workstream]);
  const history = useMemo(() => filterWs(actions.history || []), [actions.history, workstream]);
  const waitingExternal = actions.waitingExternal || [];
  const packages = actions.activeWorkPackages || [];
  const todayCount = actions.today?.length || 0;
  const todayFull = todayCount >= 3;

  const acceptMove = (a) => {
    if (todayFull) return;
    patch('top3', a.id, {
      selectedForToday: true,
      todayRank: todayCount + 1,
      status: 'Today',
    });
    fireCrmWriteback(crmWritebackForAccept(a));
  };

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>Execution Queue</h2>
          <Chip>{todayCount} / 3 today</Chip>
          {actions.overdue?.length > 0 && <Chip tone="red">{actions.overdue.length} overdue</Chip>}
          <div className="spacer" />
          <select value={workstream} onChange={(e) => setWorkstream(e.target.value)} style={{ width: 160 }}>
            <option value="All">All workstreams</option>
            {workstreamOptions.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
          <button className="btn sm primary" onClick={() => setModal({ isNew: true })}>Add move</button>
        </div>
        <div className="sub">
          Bridge45 maintains this queue. Accept 1–3 moves for Today. Manual input is the exception.
        </div>
      </div>

      <section className="grid g4">
        <Card>
          <Stat label="Today" value={<span className="num">{todayCount} / 3</span>} size="sm" foot="Committed moves" />
        </Card>
        <Card>
          <Stat label="Ready Next" value={<span className="num">{actions.readyNext?.length || 0}</span>} size="sm" foot="Bridge-ranked" />
        </Card>
        <Card>
          <Stat label="Waiting External" value={<span className="num">{waitingExternal.length}</span>} size="sm" foot="Commitments outstanding" />
        </Card>
        <Card>
          <Stat
            label="Blocked"
            value={<span className="num">{actions.blocked?.length || 0}</span>}
            size="sm"
            tone={actions.blocked?.length ? 'red' : undefined}
            foot="Named dependency"
          />
        </Card>
      </section>

      {d.sprint.nextGate && (
        <section className="section">
          <Banner>
            Next gate is {d.sprint.nextGate.label}. {actions.forNextGate?.length || 0} open move(s) are marked for it.
          </Banner>
        </section>
      )}

      <TodayCommitted go={go} showQueueLink={false} />

      <QueueSection
        title="Ready Next"
        term="founderCommitment"
        hint="Highest-value moves Bridge45 believes are executable after Today's commitments."
        count={readyNext.length}
        empty="No ranked commercial moves yet. Import accounts or accept a named next step from The Bridge."
      >
        {readyNext.map((a) => (
          <MoveRow
            key={a.id}
            move={a}
            today={d.sprint}
            onOpen={() => setModal(a)}
            extra={
              <button className="btn sm primary" disabled={todayFull} onClick={() => acceptMove(a)}>
                Accept
              </button>
            }
          />
        ))}
      </QueueSection>

      <QueueSection
        title="Waiting External"
        hint="We have already acted. The commercial object waits; a dated follow-up is in Ready Next when due."
        count={waitingExternal.length}
        empty="Nothing waiting on a counterparty."
      >
        {waitingExternal.map((w) => (
          <div className="queue-row" key={`${w.kind}-${w.id}`}>
            <div style={{ flex: 1 }}>
              <div className="t">{w.label}</div>
              <div className="dim tiny" style={{ marginTop: 4 }}>
                {w.kind} · {w.waitingFor || 'Named reply outstanding'}
                {w.waitingUntil ? ` · due ${fmtDate(w.waitingUntil)}` : ''}
              </div>
            </div>
            <Chip>Waiting External</Chip>
          </div>
        ))}
      </QueueSection>

      <QueueSection
        title="Blocked"
        hint="Cannot proceed until a named dependency clears."
        count={blocked.length}
        empty="No blocked moves."
      >
        {blocked.map((a) => (
          <MoveRow key={a.id} move={a} today={d.sprint} onOpen={() => setModal(a)} />
        ))}
      </QueueSection>

      <div className="queue-divider" />

      <QueueSection
        title="Initialization"
        hint="Setup remaining before commercial ranking is trustworthy. Not Today."
        count={initialization.length}
        empty="Initialization complete — or remaining items live as work packages."
      >
        {initialization.map((a) => (
          <MoveRow key={a.id} move={a} today={d.sprint} onOpen={() => setModal(a)} />
        ))}
      </QueueSection>

      <section className="section">
        <SectionHead
          title="Work Packages"
          hint="Multi-day outcomes Bridge45 maintains. Collapsed — inspect only if something is wrong."
          right={
            <button className="btn sm ghost" onClick={() => setPackagesOpen((v) => !v)}>
              {packagesOpen ? 'Hide' : `Show ${packages.length} active`}
            </button>
          }
        />
        {packagesOpen && packages.length === 0 && <Empty title="No active work packages." />}
        {packagesOpen && packages.map((p) => (
          <div className="queue-row" key={p.id}>
            <div style={{ flex: 1 }}>
              <div className="t">{p.title}</div>
              <div className="dim tiny" style={{ marginTop: 4 }}>
                {workPackageContext(p)} · {p.completionCondition}
              </div>
            </div>
            <Chip>{p.setup ? 'Initialization' : p.status || 'Ready'}</Chip>
          </div>
        ))}
      </section>

      <section className="section">
        <SectionHead
          title="History"
          hint="What we did and what evidence it produced."
          right={
            <button className="btn sm ghost" onClick={() => setHistoryOpen((v) => !v)}>
              {historyOpen ? 'Hide' : `Show ${history.length}`}
            </button>
          }
        />
        {historyOpen && history.length === 0 && <Empty title={EMPTY_COPY.history} />}
        {historyOpen && history.map((a) => (
          <MoveRow key={a.id} move={a} today={d.sprint} onOpen={() => setModal(a)} history />
        ))}
      </section>

      {modal && <ActionForm draft={modal} onClose={() => setModal(null)} />}
    </>
  );
}

function QueueSection({ title, hint, count, empty, children, term }) {
  const list = React.Children.toArray(children);
  return (
    <section className="section">
      <SectionHead
        title={title}
        hint={hint}
        term={term}
        right={<Chip>{count}</Chip>}
      />
      {list.length === 0 ? <Empty title={empty} /> : <Card flush>{list}</Card>}
    </section>
  );
}

function MoveRow({ move: a, today, onOpen, extra, history }) {
  const overdue = !history && isOverdue(a, today);
  return (
    <div className="queue-row">
      <div style={{ flex: 1 }}>
        <div className="t">{a.text}</div>
        <div className="dim tiny" style={{ marginTop: 4 }}>
          {a.workstream}
          {a.source ? ` · ${sourceLabel(a.source)}` : ''}
          {a.linkedLabel ? ` · ${a.linkedLabel}` : ''}
          {a.whyNow ? ` · ${a.whyNow}` : ''}
          {a.blocker ? ` · blocked by ${a.blocker}` : ''}
          {history && (a.completionResult || a.disposition) ? ` · ${a.disposition || a.completionResult}` : ''}
          {history && a.evidenceCreated ? ` · created ${a.evidenceCreated}` : ''}
        </div>
      </div>
      {a.priority && <Chip tone={priorityTone(a.priority)}>{a.priority}</Chip>}
      {a.expectedEvidence === 'OPS' ? <Chip>Internal enabler</Chip> : a.expectedEvidence ? <EvidenceBadge level={a.expectedEvidence} /> : null}
      <span className="num tiny" style={overdue ? { color: 'var(--red)' } : undefined}>
        {a.dueDate ? fmtDate(a.dueDate) : ''}
      </span>
      <Chip tone={statusTone(a.status)}>{a.status}</Chip>
      {extra}
      <button className="btn sm ghost" onClick={onOpen}>{history ? 'View' : 'Edit'}</button>
    </div>
  );
}
